/**
 * Centralized API response parser to sync frontend messaging with backend API messages
 * Handles all backend response patterns from the TeeLogix API
 */

export interface ParsedApiResponse {
  message: string;
  type: "success" | "error" | "warning" | "info";
  exceptionId?: string;
  fieldErrors?: Record<string, string>;
  originalData?: any;
}

export interface ApiErrorDetails {
  message: string;
  field?: string;
  exceptionId?: string;
  statusCode?: number;
}

/**
 * Parse backend API error responses with comprehensive handling
 * Handles multiple error formats from Django REST Framework
 */
export const parseApiError = (error: any): ApiErrorDetails => {
  const response = error.response;
  const data = response?.data;
  const status = response?.status;

  // Handle network errors
  if (!response) {
    return {
      message:
        "Unable to connect to the server. Please check your internet connection.",
      statusCode: 0,
    };
  }

  // Extract exception ID for tracking
  const exceptionId = data?.exception_id;

  // Priority 1: Handle field-specific validation errors (Django style)
  if (data && typeof data === "object") {
    for (const [fieldName, messages] of Object.entries(data)) {
      if (Array.isArray(messages) && messages.length > 0) {
        // Skip non-field keys
        if (
          !["message", "error", "exception_id", "detail", "overlaps"].includes(
            fieldName
          )
        ) {
          return {
            message: messages[0] as string,
            field: fieldName,
            exceptionId,
            statusCode: status,
          };
        }
      }
    }
  }

  // Priority 2: Handle structured error responses
  if (data?.message) {
    return {
      message: data.message,
      exceptionId,
      statusCode: status,
    };
  }

  if (data?.error) {
    return {
      message: data.error,
      exceptionId,
      statusCode: status,
    };
  }

  // Priority 3: Handle DRF detail errors
  if (data?.detail) {
    return {
      message: Array.isArray(data.detail) ? data.detail.join(", ") : data.detail,
      exceptionId,
      statusCode: status,
    };
  }

  // Priority 4: Handle non_field_errors (DRF validation)
  if (data?.non_field_errors && Array.isArray(data.non_field_errors)) {
    return {
      message: data.non_field_errors[0],
      exceptionId,
      statusCode: status,
    };
  }

  // Priority 5: Status code specific messages
  switch (status) {
    case 400:
      return {
        message:
          "The information you provided is invalid. Please check and try again.",
        exceptionId,
        statusCode: status,
      };
    case 401:
      return {
        message: "Your session has expired. Please sign in again.",
        exceptionId,
        statusCode: status,
      };
    case 403:
      return {
        message: "You don't have permission to perform this action.",
        exceptionId,
        statusCode: status,
      };
    case 404:
      return {
        message: "The requested resource could not be found.",
        exceptionId,
        statusCode: status,
      };
    case 429:
      return {
        message: "Too many requests. Please wait a moment and try again.",
        exceptionId,
        statusCode: status,
      };
    case 500:
      return {
        message:
          "We're experiencing technical difficulties. Please try again later.",
        exceptionId,
        statusCode: status,
      };
    default:
      return {
        message: "An unexpected error occurred. Please try again.",
        exceptionId,
        statusCode: status,
      };
  }
};

/**
 * Parse success responses from the backend
 */
export const parseApiSuccess = (response: any): ParsedApiResponse => {
  const data = response.data;

  return {
    message: data?.message || "Operation completed successfully.",
    type: "success",
    originalData: data,
  };
};

/**
 * Extract all field-specific errors from validation responses
 */
export const extractFieldErrors = (error: any): Record<string, string> => {
  const data = error.response?.data;
  const fieldErrors: Record<string, string> = {};

  if (data && typeof data === "object") {
    for (const [fieldName, messages] of Object.entries(data)) {
      if (Array.isArray(messages) && messages.length > 0) {
        // Skip non-field keys
        if (
          ![
            "message",
            "error",
            "exception_id",
            "detail",
            "non_field_errors",
          ].includes(fieldName)
        ) {
          fieldErrors[fieldName] = messages[0] as string;
        }
      }
    }
  }

  return fieldErrors;
};

/**
 * Check if error response has field-specific validation errors
 */
export const hasFieldErrors = (error: any): boolean => {
  return Object.keys(extractFieldErrors(error)).length > 0;
};

/**
 * Get user-friendly message based on error type and context
 */
export const getContextualErrorMessage = (
  error: any,
  context:
    | "login"
    | "register"
    | "booking"
    | "payment"
    | "profile"
    | "general" = "general"
): string => {
  const parsed = parseApiError(error);

  // If we have a specific backend message, use it
  if (
    parsed.message &&
    !parsed.message.includes("An unexpected error occurred")
  ) {
    return parsed.message;
  }

  // Fallback contextual messages
  const contextMessages = {
    login: "Unable to sign in. Please check your credentials and try again.",
    register:
      "Registration failed. Please check your information and try again.",
    booking:
      "Unable to process your booking. Please try again or contact support.",
    payment:
      "Payment processing failed. Please check your payment details and try again.",
    profile: "Unable to update your profile. Please try again.",
    general: "Something went wrong. Please try again.",
  };

  return contextMessages[context];
};

/**
 * Format error for logging/debugging
 */
export const formatErrorForLogging = (
  error: any,
  context?: string
): Record<string, any> => {
  const parsed = parseApiError(error);

  return {
    context,
    message: parsed.message,
    field: parsed.field,
    exceptionId: parsed.exceptionId,
    statusCode: parsed.statusCode,
    timestamp: new Date().toISOString(),
    url: error.config?.url,
    method: error.config?.method?.toUpperCase(),
    requestData: error.config?.data ? JSON.parse(error.config.data) : null,
  };
};
